import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, RefreshCw, Search, Edit, Trash2, Settings, TestTube } from 'lucide-react'
import { toast } from 'react-toastify'
import { appsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import AppModal from '../components/AppModal'
import ConfirmDialog from '../components/ConfirmDialog'

function Apps() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedApp, setSelectedApp] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteApp, setDeleteApp] = useState(null)
  const [testingApp, setTestingApp] = useState(null)
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: apps, isLoading } = useQuery('apps', appsAPI.getApps)
  
  const syncMutation = useMutation(appsAPI.syncApps, {
    onSuccess: (data) => {
      toast.success(data.data.message)
      queryClient.invalidateQueries('apps')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Sync failed')
    }
  })

  const deleteMutation = useMutation(appsAPI.deleteApp, {
    onSuccess: () => {
      toast.success('App deleted successfully')
      queryClient.invalidateQueries('apps')
      setDeleteApp(null)
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Delete failed')
    }
  })

  const testWebhookMutation = useMutation(appsAPI.testWebhook, {
    onSuccess: (data) => {
      const results = data.data.results
      const successCount = Object.values(results).filter(r => r.success).length
      const totalCount = Object.keys(results).length
      
      if (successCount === totalCount) {
        toast.success('All webhooks are working correctly')
      } else {
        toast.warning(`${successCount}/${totalCount} webhooks responded correctly`)
      }
      setTestingApp(null)
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Webhook test failed')
      setTestingApp(null)
    }
  })

  const filteredApps = apps?.data?.apps?.filter(app =>
    app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  const handleEdit = (app) => {
    setSelectedApp(app)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setSelectedApp(null)
    setIsModalOpen(true)
  }

  const handleDelete = (app) => {
    setDeleteApp(app)
  }

  const handleTestWebhook = (app) => {
    setTestingApp(app._id)
    testWebhookMutation.mutate(app._id)
  }

  const confirmDelete = () => {
    if (deleteApp) {
      deleteMutation.mutate(deleteApp._id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-900">Applications</h1>
          <p className="mt-1 text-sm text-primary-600">
            Manage your applications and webhooks
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3">
          {user?.role === 'admin' && (
            <>
              <button
                type="button"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isLoading}
                className="btn-secondary"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {syncMutation.isLoading ? 'Syncing...' : 'Sync Data'}
              </button>
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                New App
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-primary-400" />
          </div>
          <input
            type="text"
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Apps Grid */}
      {filteredApps.length === 0 ? (
        <div className="text-center py-12">
          <Settings className="mx-auto h-12 w-12 text-primary-400" />
          <h3 className="mt-2 text-sm font-medium text-primary-900">No applications</h3>
          <p className="mt-1 text-sm text-primary-500">
            {searchTerm ? 'No applications match your search.' : 'Get started by creating a new application.'}
          </p>
          {user?.role === 'admin' && !searchTerm && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                New App
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredApps.map((app) => (
            <div key={app._id} className="card group hover:shadow-md transition-shadow">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <Settings className="h-5 w-5 text-primary-600" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-primary-900 truncate">
                        {app.name}
                      </p>
                      <p className="text-sm text-primary-500 truncate">
                        {app.speechSynthesisVendor || 'No TTS vendor'}
                      </p>
                    </div>
                  </div>
                  <span className={`status-badge ${app.isActive ? 'status-active' : 'status-inactive'}`}>
                    {app.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {app.description && (
                  <p className="mt-3 text-sm text-primary-600 line-clamp-2">
                    {app.description}
                  </p>
                )}

                <div className="mt-4 space-y-2">
                  <div className="text-xs text-primary-500">
                    Webhook: {app.callingWebhook ? 'Configured' : 'Not set'}
                  </div>
                  <div className="text-xs text-primary-500">
                    STT: {app.speechRecognitionVendor || 'Not configured'}
                  </div>
                  {app.lastSyncedAt && (
                    <div className="text-xs text-primary-500">
                      Last synced: {new Date(app.lastSyncedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                {user?.role === 'admin' && (
                  <div className="mt-4 flex justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleTestWebhook(app)}
                      disabled={testingApp === app._id}
                      className="p-1 text-blue-600 hover:text-blue-900"
                      title="Test Webhooks"
                    >
                      {testingApp === app._id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <TestTube className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(app)}
                      className="p-1 text-primary-600 hover:text-primary-900"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(app)}
                      className="p-1 text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AppModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        app={selectedApp}
      />

      <ConfirmDialog
        isOpen={!!deleteApp}
        onClose={() => setDeleteApp(null)}
        onConfirm={confirmDelete}
        title="Delete Application"
        message={`Are you sure you want to delete "${deleteApp?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="btn-danger"
      />
    </div>
  )
}

export default Apps